import { Globe, Route, Settings, Table2, Waypoints, Wifi } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FirewallArpTable from "./FirewallArpTable";
import FirewallDhcpLeases from "./FirewallDhcpLeases";
import FirewallStaticRoutes from "./FirewallStaticRoutes";
import FirewallRoutingTable from "./FirewallRoutingTable";
import FirewallVipIPv4 from "./FirewallVipIPv4";
import NetworkConfig from "./NetworkConfig";

// ── Tab Config ────────────────────────────────────────────────────────────────
const TABS = [
    { id: "arp", label: "ARP Table", icon: Globe, component: FirewallArpTable },
    { id: "dhcp", label: "DHCP Leases", icon: Wifi, component: FirewallDhcpLeases },
    { id: "static_routes", label: "Static Routes", icon: Route, component: FirewallStaticRoutes },
    { id: "routing_table", label: "Routing Table", icon: Table2, component: FirewallRoutingTable },
    { id: "vip_ipv4", label: "VIP IPv4", icon: Waypoints, component: FirewallVipIPv4 },
    { id: "network_config", label: "Network Config", icon: Settings, component: NetworkConfig },
] as const;

type TabId = typeof TABS[number]["id"];
const VALID_IDS = TABS.map(t => t.id) as string[];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function FirewallNetworkMain({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const rawTab = searchParams.get("network_tab");
    const activeTab: TabId = (rawTab && VALID_IDS.includes(rawTab) ? rawTab : "arp") as TabId;

    const setActiveTab = (id: TabId) => {
        setSearchParams(prev => {
            prev.set("network_tab", id);
            return prev;
        });
    };

    const ActiveComponent = TABS.find(t => t.id === activeTab)!.component;

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-gray-700 mb-4">
                {TABS.map(({ id, label, icon: Icon }) => {
                    const isActive = activeTab === id;
                    return (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`relative flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-all
                                ${isActive
                                    ? "text-purple-400"
                                    : "text-gray-200 hover:text-gray-300"
                                }`}
                        >
                            <Icon size={12} />
                            {label}
                            {isActive && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500 rounded-t-full" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            <div className="animate-fade-in">
                <ActiveComponent asset_id={asset_id} client_id={client_id} />
            </div>
        </div>
    );
}
